/**
 * 74.20 (DECISIONS 427 (4), W0; the sheet design_sheets/riverbeds.html): **the drainage lines** — the world sheet's chains
 * (core/worldSheet.ts) as lines on the person's ground, for the beds drawn on it (creeks, dry and stony beds, broad beds
 * with banks, reed belts; no water: 427 (1)) and for lane I's creek cut through the woods (426, 434), which follows the same
 * lines. One definition, read by both.
 *
 * Each chain becomes a line of points `stepPeople` apart: a Catmull-Rom curve through its weld vertices (on the unit sphere),
 * with a meander of its own (a sine along it, its wave and amplitude by the bed's width, damped on steep valley sides and to
 * nothing at both ends, so a tributary meets its parent's line where the parent passes). Per point, from the sheet:
 * - **the class, by the local flow** (not the chain's order, which saturates at 8× the first chain's flow): a *line* (a
 *   gully or drainage line, a person wide) under `bedFrom`, a *bed* to `broadFrom`, a *broad* bed with banks above;
 * - **the width** (people), by the flow within its class;
 * - **the kind, by the climate**: *damp* where the ground is wet (darker, reed-belted), a *wash* where it is dry (pale
 *   gravel), *stony* between, and on the heights — read from the wet mask less the near-water term every chain carries;
 * - **reeds**: on a damp bed's or broad bed's flat reach (grade under `reed.grade`), and on the last `reed.mouthPeople` of a
 *   line into a basin or a lake's shore;
 * - **under a lake**: points under a lake basin's spill. The lake is the sim's basin and is not drawn (427 (1)), so the bed is
 *   drawn there too and the flag is for a later W1; on the megaplanets 24–72 % of the chains' length lies under one.
 *
 * `drainageAt` finds the line near a direction: the signed distance across it in people, its half-width and banks, the
 * class, the kind and the reeds. A bucket per weld vertex (the planet's mesh vertex nearest, core/surface.ts), its ring
 * searched. Pure (dmath), cached per world sheet and person. Presentation and the woods' arrangement read it; no state.
 */
import type { PlanetState, Vec3 } from '../types';
import { worldSheetOf, WORLD_SHEET, type WorldSheet } from './worldSheet';
import { nearestSurfaceVertex } from './surface';
import { planetRouteGraph } from './planetRoutes';
import { dsin, dlog, dpow } from './dmath';

export type DrainageClass = 'line' | 'bed' | 'broad';
export type BedKind = 'damp' | 'stony' | 'wash';
export const DRAINAGE_CLASSES: DrainageClass[] = ['line', 'bed', 'broad'];
export const BED_KINDS: BedKind[] = ['damp', 'stony', 'wash'];

/** The dials, in people unless said. */
export const DRAINAGE_LINES = {
  /** the points' spacing along a line */
  stepPeople: 3,
  /** the class by the local flow (the sheet's: 12 at a chain's head, ~3,500 at the biggest mouths) */
  bedFrom: 40, broadFrom: 400, flowTop: 3500,
  /** the width within each class, from its least flow to its most */
  width: { line: [0.6, 1.2], bed: [2, 6], broad: [8, 20] } as Record<DrainageClass, [number, number]>,
  /** the bank's band past the bed's edge */
  bank: { line: 0.4, bed: 1.2, broad: 3 } as Record<DrainageClass, number>,
  /** the meander: amplitude × the width (capped), wave × the width, gone at the sheet's slope `flatTo`; damped over this share of a wave at both ends */
  meander: { amp: 0.6, ampMax: 6, wave: 12, flatTo: 0.35, ends: 0.5 },
  /** the kind, by the ground's own wet without the bed's (the wet mask less its near-water term, which a chain's vertex
   * always carries in full): damp from `dampFrom`, a wash under `washBelow`, stony between and at the sheet's high band.
   * (On the chains, the median of that read: viridis 0.45, thalia and bruma 0.34, tephra 0.17, sonora 0.11, ochrus 0.07.) */
  dampFrom: 0.25, washBelow: 0.12,
  /** reeds: a damp bed's reach flatter than this grade, the last stretch into a basin or a lake */
  reed: { grade: 0.04, mouthPeople: 60 },
};

export interface DrainageLine {
  /** the chain's index in the sheet */
  chain: number;
  /** per point: the direction (3 a point), the width (people), the class (an index into DRAINAGE_CLASSES), the kind (into BED_KINDS), reeds (1), under a lake (1) */
  dirs: Float64Array; width: Float32Array; cls: Uint8Array; kind: Uint8Array; reed: Uint8Array; lake: Uint8Array;
}

export interface DrainageLines {
  lines: DrainageLine[];
  /** the person (world units) the widths are in, and the world's radius in people */
  person: number; rPeople: number;
  /** the buckets: per weld vertex, the points nearest it, as (line, point) pairs, CSR */
  start: Int32Array; items: Int32Array;
  /** for the cost line: the points, and the build (ms; 0 where the clock is not available) */
  points: number; buildMs: number;
}

export interface DrainageHit {
  line: number; point: number;
  /** the signed distance across the line (people; + on the line's left looking downstream), and how far it is to its bed's edge (≤ 0 inside) */
  across: number; edge: number;
  /** half the bed's width and the bank's band past it (people) */
  half: number; bank: number;
  cls: DrainageClass; kind: BedKind; reed: boolean; lake: boolean;
}

const caches = new WeakMap<WorldSheet, Map<number, DrainageLines>>();

/** The planet's drainage lines (cached per world sheet and person). */
export function drainageLinesOf(planet: PlanetState, person: number): DrainageLines {
  const sheet = worldSheetOf(planet);
  let byPerson = caches.get(sheet);
  if (!byPerson) { byPerson = new Map(); caches.set(sheet, byPerson); }
  let out = byPerson.get(person);
  if (!out) { out = buildDrainageLines(planet, sheet, person); byPerson.set(person, out); }
  return out;
}

/** The lines built (pure). */
export function buildDrainageLines(planet: PlanetState, sheet: WorldSheet, person: number): DrainageLines {
  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  const D = DRAINAGE_LINES, g = sheet.graph, V = g.count, d = g.dirs, R = planet.radius, rPeople = R / person;
  const owner = new Int32Array(V).fill(-1), pointAt: Array<Map<number, number>> = [];
  const lines: DrainageLine[] = [];
  const unit = (x: number, y: number, z: number): [number, number, number] => { const l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; };
  const lakeAt = (v: number) => { const b = sheet.basins[sheet.basinOf[v]]; return !!b && b.lake && sheet.radius[v] <= b.spillRadius; };
  const flowLog = (f: number) => Math.min(1, Math.max(0, dlog(Math.max(1, f) / WORLD_SHEET.flowFrom) / dlog(D.flowTop / WORLD_SHEET.flowFrom)));
  const classOf = (f: number) => (f < D.bedFrom ? 0 : f < D.broadFrom ? 1 : 2);
  const widthOf = (f: number, c: number) => {
    const lo = [WORLD_SHEET.flowFrom, D.bedFrom, D.broadFrom][c], hi = [D.bedFrom, D.broadFrom, D.flowTop][c];
    const t = Math.min(1, Math.max(0, dlog(Math.max(lo, f) / lo) / dlog(hi / lo)));
    const w = D.width[DRAINAGE_CLASSES[c]];
    return w[0] + (w[1] - w[0]) * t;
  };
  const kindOf = (v: number) => { const own = sheet.moisture[v] - WORLD_SHEET.nearWater; return sheet.elevation[v] >= WORLD_SHEET.highAt ? 1 : own >= D.dampFrom ? 0 : own < D.washBelow ? 2 : 1; };
  sheet.chains.forEach((c, ci) => {
    const vs = c.vertices, n = vs.length;
    for (const v of vs) if (owner[v] < 0) owner[v] = ci;
    pointAt.push(new Map());
    if (n < 2) { lines.push(empty(ci)); return; }
    // the curve: Catmull-Rom through the vertices, `stepPeople` apart
    const P: number[] = [], F: number[] = [], NV: number[] = [], S: number[] = [];   // dirs, flow, the nearer vertex, the arc so far (people)
    let s = 0;
    for (let k = 0; k + 1 < n; k++) {
      const a = vs[Math.max(0, k - 1)], b = vs[k], e = vs[k + 1], f = vs[Math.min(n - 1, k + 2)];
      const ex = d[3 * e] - d[3 * b], ey = d[3 * e + 1] - d[3 * b + 1], ez = d[3 * e + 2] - d[3 * b + 2];
      const len = Math.sqrt(ex * ex + ey * ey + ez * ez) * rPeople, steps = Math.max(1, Math.ceil(len / D.stepPeople));
      for (let i = 0; i < steps; i++) {
        const t = i / steps, t2 = t * t, t3 = t2 * t;
        const w0 = -0.5 * t3 + t2 - 0.5 * t, w1 = 1.5 * t3 - 2.5 * t2 + 1, w2 = -1.5 * t3 + 2 * t2 + 0.5 * t, w3 = 0.5 * t3 - 0.5 * t2;
        const q = unit(w0 * d[3 * a] + w1 * d[3 * b] + w2 * d[3 * e] + w3 * d[3 * f], w0 * d[3 * a + 1] + w1 * d[3 * b + 1] + w2 * d[3 * e + 1] + w3 * d[3 * f + 1], w0 * d[3 * a + 2] + w1 * d[3 * b + 2] + w2 * d[3 * e + 2] + w3 * d[3 * f + 2]);
        P.push(q[0], q[1], q[2]);
        const fb = Math.max(1, sheet.flow[b]), fe = Math.max(1, sheet.flow[e]);
        F.push(fb * dpow(fe / fb, t)); NV.push(t < 0.5 ? b : e); S.push(s + t * len);
        if (i === 0) pointAt[ci].set(b, P.length / 3 - 1);
      }
      s += len;
    }
    // the last point: the mouth (a parent's line where it passes, so the two meet) or the chain's own end
    const last = vs[n - 1], parent = owner[last] !== ci ? owner[last] : -1, pi = parent >= 0 ? pointAt[parent].get(last) : undefined;
    if (parent >= 0 && pi !== undefined) { const pd = lines[parent].dirs; P.push(pd[3 * pi], pd[3 * pi + 1], pd[3 * pi + 2]); }
    else P.push(d[3 * last], d[3 * last + 1], d[3 * last + 2]);
    F.push(Math.max(1, sheet.flow[last])); NV.push(last); S.push(s);
    if (!pointAt[ci].has(last)) pointAt[ci].set(last, P.length / 3 - 1);
    const m = P.length / 3, total = s;
    const dirs = new Float64Array(m * 3), width = new Float32Array(m), cls = new Uint8Array(m), kind = new Uint8Array(m), reed = new Uint8Array(m), lake = new Uint8Array(m);
    const phase = ((vs[0] * 2654435761) >>> 0) / 4294967296 * 2 * Math.PI;
    for (let i = 0; i < m; i++) {
      const c2 = classOf(F[i]); cls[i] = c2; width[i] = widthOf(F[i], c2); kind[i] = kindOf(NV[i]); lake[i] = lakeAt(NV[i]) ? 1 : 0;
      // the meander, across the line (not at the mouth's point: it is the parent's)
      let x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
      if (i < m - 1 || parent < 0) {
        const j0 = Math.max(0, i - 1), j1 = Math.min(m - 1, i + 1);
        const tx = P[3 * j1] - P[3 * j0], ty = P[3 * j1 + 1] - P[3 * j0 + 1], tz = P[3 * j1 + 2] - P[3 * j0 + 2];
        const nrm = unit(ty * z - tz * y, tz * x - tx * z, tx * y - ty * x);
        const wave = D.meander.wave * width[i], ends = D.meander.ends * wave;
        const ramp = Math.min(1, S[i] / ends, (total - S[i]) / ends);
        const flat = Math.max(0, 1 - sheet.slope[NV[i]] / D.meander.flatTo);
        const amp = Math.min(D.meander.ampMax, D.meander.amp * width[i]) * Math.max(0, ramp) * flat;
        const off = amp * dsin(2 * Math.PI * S[i] / wave + phase) / rPeople;
        [x, y, z] = unit(x + nrm[0] * off, y + nrm[1] * off, z + nrm[2] * off);
      }
      dirs[3 * i] = x; dirs[3 * i + 1] = y; dirs[3 * i + 2] = z;
    }
    // reeds: a damp bed's flat reach, and the last stretch into a basin's sink or a lake's shore
    const sinkMouth = sheet.downhill[last] < 0;
    let shoreAt = -1;
    for (let i = 0; i < m; i++) if (lake[i]) { shoreAt = i; break; }
    for (let i = 0; i < m; i++) {
      if (kind[i] !== 0 || lake[i]) continue;
      const j = Math.min(m - 1, i + 1), run = Math.max(1e-6, S[j] - S[i] || D.stepPeople);
      const grade = Math.abs(sheet.radius[NV[i]] - sheet.radius[NV[j]]) / person / run;
      const toMouth = shoreAt >= 0 ? S[shoreAt] - S[i] : sinkMouth ? total - S[i] : Infinity;
      if ((cls[i] > 0 && grade < D.reed.grade) || (toMouth >= 0 && toMouth <= D.reed.mouthPeople)) reed[i] = 1;
    }
    lines.push({ chain: ci, dirs, width, cls, kind, reed, lake });
  });
  // the buckets: each point to its nearest weld vertex (the mesh vertex nearest, welded)
  const counts = new Int32Array(V + 1), pairs: number[] = [];
  lines.forEach((ln, li) => {
    for (let i = 0; i < ln.width.length; i++) {
      const v = g.nodeOf[nearestSurfaceVertex(planet, { x: ln.dirs[3 * i], y: ln.dirs[3 * i + 1], z: ln.dirs[3 * i + 2] })];
      pairs.push(v, li, i); counts[v + 1]++;
    }
  });
  const start = new Int32Array(V + 1);
  for (let v = 0; v < V; v++) start[v + 1] = start[v] + counts[v + 1];
  const fill = start.slice(0, V), items = new Int32Array(2 * start[V]);
  for (let k = 0; k < pairs.length; k += 3) { const at = fill[pairs[k]]++; items[2 * at] = pairs[k + 1]; items[2 * at + 1] = pairs[k + 2]; }
  const buildMs = typeof performance !== 'undefined' ? performance.now() - t0 : 0;
  return { lines, person, rPeople, start, items, points: pairs.length / 3, buildMs };
}

function empty(chain: number): DrainageLine {
  return { chain, dirs: new Float64Array(0), width: new Float32Array(0), cls: new Uint8Array(0), kind: new Uint8Array(0), reed: new Uint8Array(0), lake: new Uint8Array(0) };
}

/**
 * The drainage line nearest `dir` whose bed or bank reaches within `reachPeople` past its edge (null: none). Of the lines
 * there, the one `dir` is most inside (the least distance to its edge), so a creek's mouth inside a broad bed reads the
 * broad bed. Pure.
 */
export function drainageAt(planet: PlanetState, dl: DrainageLines, dir: Vec3, reachPeople = 0): DrainageHit | null {
  const g = planetRouteGraph(planet), v0 = g.nodeOf[nearestSurfaceVertex(planet, dir)];
  let best: DrainageHit | null = null;
  const consider = (v: number) => {
    for (let k = dl.start[v]; k < dl.start[v + 1]; k++) {
      const li = dl.items[2 * k], i = dl.items[2 * k + 1], m = dl.lines[li].width.length;
      for (let j = i - 1; j <= i; j++) {
        if (j < 0 || j + 1 >= m) continue;
        best = spanBest(dl, li, j, dir, reachPeople, best);
      }
    }
  };
  consider(v0);
  for (let k = g.start[v0]; k < g.start[v0 + 1]; k++) consider(g.nbr[k]);
  return best;
}

/** One span of a line: the segment from its point `j` to `j + 1`. */
export interface DrainageSpan { line: number; j: number }

/**
 * 426 (2), the woods' creek cut: the spans whose bed or bank reaches within `reachPeople` of `dir` — read once at a stand's
 * anchor with the stand's reach, then `drainageAtSpans` over them for each tree, instead of `drainageAt`'s bucket walk a tree.
 * The ring searched is two deep (the nearest vertex, its route-graph neighbours and theirs), so a point within `reachPeople`
 * of `dir` finds every span its own `drainageAt` would (its ring lies inside this one; the vertices are ~46 people apart on
 * a megaplanet and a stand reaches ~15), each span once. Pure.
 */
export function drainageSpansNear(planet: PlanetState, dl: DrainageLines, dir: Vec3, reachPeople: number): DrainageSpan[] {
  const g = planetRouteGraph(planet), v0 = g.nodeOf[nearestSurfaceVertex(planet, dir)], out: DrainageSpan[] = [], seen = new Set<number>();
  const consider = (v: number) => {
    for (let k = dl.start[v]; k < dl.start[v + 1]; k++) {
      const li = dl.items[2 * k], i = dl.items[2 * k + 1], m = dl.lines[li].width.length;
      for (let j = i - 1; j <= i; j++) {
        if (j < 0 || j + 1 >= m) continue;
        const key = li * 1048576 + j;   // (a line's points are under 2^20)
        if (seen.has(key)) continue;
        seen.add(key);
        if (spanBest(dl, li, j, dir, reachPeople, null)) out.push({ line: li, j });
      }
    }
  };
  consider(v0);
  for (let k = g.start[v0]; k < g.start[v0 + 1]; k++) {
    const w = g.nbr[k];
    consider(w);
    for (let q = g.start[w]; q < g.start[w + 1]; q++) if (g.nbr[q] !== v0) consider(g.nbr[q]);
  }
  return out;
}

/** `drainageAt`'s rule over a given list of spans: the one `dir` is most inside, within `reachPeople` past its bank (null: none). */
export function drainageAtSpans(dl: DrainageLines, dir: Vec3, spans: readonly DrainageSpan[], reachPeople = 0): DrainageHit | null {
  let best: DrainageHit | null = null;
  for (const s of spans) best = spanBest(dl, s.line, s.j, dir, reachPeople, best);
  return best;
}

/** A span's read at `dir`: the hit when its edge is within the bank and `reachPeople`, and nearer its edge than `best` (else `best` as given). */
function spanBest(dl: DrainageLines, li: number, j: number, dir: Vec3, reachPeople: number, best: DrainageHit | null): DrainageHit | null {
  const ln = dl.lines[li];
  const ax = ln.dirs[3 * j], ay = ln.dirs[3 * j + 1], az = ln.dirs[3 * j + 2];
  const sx = ln.dirs[3 * j + 3] - ax, sy = ln.dirs[3 * j + 4] - ay, sz = ln.dirs[3 * j + 5] - az;
  const px = dir.x - ax, py = dir.y - ay, pz = dir.z - az, ss = sx * sx + sy * sy + sz * sz;
  const t = ss > 0 ? Math.max(0, Math.min(1, (px * sx + py * sy + pz * sz) / ss)) : 0;
  const qx = px - sx * t, qy = py - sy * t, qz = pz - sz * t;
  const dist = Math.sqrt(qx * qx + qy * qy + qz * qz) * dl.rPeople;
  const at = t < 0.5 ? j : j + 1, c = ln.cls[at], half = 0.5 * (ln.width[j] + (ln.width[j + 1] - ln.width[j]) * t), bank = DRAINAGE_LINES.bank[DRAINAGE_CLASSES[c]];
  const edge = dist - half;
  if (edge > bank + reachPeople) return best;
  if (best && edge >= best.edge) return best;
  // the side: + on the left looking downstream (the segment × the point, along the up)
  const cx = sy * pz - sz * py, cy = sz * px - sx * pz, cz = sx * py - sy * px, side = cx * dir.x + cy * dir.y + cz * dir.z >= 0 ? 1 : -1;
  return { line: li, point: at, across: side * dist, edge, half, bank, cls: c === 0 ? 'line' : c === 1 ? 'bed' : 'broad', kind: BED_KINDS[ln.kind[at]], reed: ln.reed[at] === 1, lake: ln.lake[at] === 1 };
}

